import { Badge, cn } from "@nexus/ui";
import type { KeyboardEvent } from "react";
import { useTranslation } from "react-i18next";

import { OrderLineVideoLabelEditor } from "@/features/marketplace/components/OrderLineVideoLabelEditor";
import { ScriptReviewAction } from "@/features/marketplace/components/ScriptReviewAction";
import { videoReviewBadgeVariant } from "@/features/marketplace/lib/line-status-display";
import {
  orderLineVideoDisplayName,
  orderLineVideoKey,
} from "@/features/marketplace/lib/order-line-videos";
import type { OrderLineVideo } from "@/features/marketplace/types/marketplace.types";
import { useUpdateOrderScriptVideoLabel } from "@/lib/queries/marketplace.queries";

import {
  ADMIN_ORDERS_CELL_ACTIONS_CLASS,
  ADMIN_ORDERS_CELL_DUE_CLASS,
  ADMIN_ORDERS_CELL_PARTNER_CLASS,
  ADMIN_ORDERS_CELL_SCRIPTS_CLASS,
  ADMIN_ORDERS_CELL_SENT_CLASS,
  ADMIN_ORDERS_CELL_STATUS_CLASS,
  ADMIN_ORDERS_CELL_TYPE_CLASS,
  ADMIN_ORDERS_GRID_CLASS,
  ADMIN_ORDERS_ROW_PADDING_CLASS,
} from "./admin-orders-grid";

interface AdminOrderVideoRowProps {
  orderId: string;
  orderScriptId: string;
  video: OrderLineVideo;
  scriptTitle: string;
  rowIndex: number;
  selected: boolean;
  onSelect: () => void;
}

export function AdminOrderVideoRow({
  orderId,
  orderScriptId,
  video,
  scriptTitle,
  rowIndex,
  selected,
  onSelect,
}: AdminOrderVideoRowProps) {
  const { t } = useTranslation();
  const updateLabel = useUpdateOrderScriptVideoLabel();
  const label = orderLineVideoDisplayName(video, t);

  const handleKeyDown = (event: KeyboardEvent<HTMLLIElement>) => {
    if (event.target !== event.currentTarget) return;
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      onSelect();
    }
  };

  return (
    <li
      id={`admin-video-${orderScriptId}-${orderLineVideoKey(video)}`}
      role="row"
      aria-level={3}
      aria-rowindex={rowIndex}
      aria-selected={selected}
      data-admin-video-row
      data-tree-row
      tabIndex={0}
      onClick={onSelect}
      onKeyDown={handleKeyDown}
      className={cn(
        ADMIN_ORDERS_GRID_CLASS,
        ADMIN_ORDERS_ROW_PADDING_CLASS,
        "relative min-h-11 scroll-mt-20 cursor-pointer py-1.5 outline-none transition-colors hover:bg-muted/25 focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring/50",
        selected ? "bg-primary-subtle/60" : "bg-muted/10",
      )}
    >
      <span aria-hidden role="gridcell" />
      <span aria-hidden role="gridcell" />
      <span
        aria-hidden
        className="absolute bottom-0 left-[3.85rem] top-0 w-px bg-border"
      />
      <span
        aria-hidden
        className="absolute left-[3.85rem] top-1/2 h-px w-5 bg-border"
      />
      <span
        aria-hidden
        className="absolute bottom-0 left-[5.35rem] top-0 w-px bg-border"
      />
      <span
        aria-hidden
        className="absolute left-[5.35rem] top-1/2 h-px w-4 bg-border"
      />

      <div role="gridcell" className="min-w-0 pl-14">
        <OrderLineVideoLabelEditor
          video={video}
          isPending={updateLabel.isPending}
          onSave={(nextLabel) => {
            if (!video.id) return;
            updateLabel.mutate({
              orderId,
              orderScriptId,
              deliverableId: video.id,
              label: nextLabel,
              admin: true,
            });
          }}
        />
        {video.revisionNote?.trim() ? (
          <p className="mt-0.5 line-clamp-2 text-[11px] leading-4 text-warning-subtle-foreground">
            {video.revisionNote}
          </p>
        ) : null}
      </div>

      <span role="gridcell" aria-hidden className={ADMIN_ORDERS_CELL_PARTNER_CLASS} />
      <span role="gridcell" aria-hidden className={ADMIN_ORDERS_CELL_TYPE_CLASS} />
      <span
        role="gridcell"
        aria-hidden
        className={cn(
          ADMIN_ORDERS_CELL_SCRIPTS_CLASS,
          "text-xs text-muted-foreground/45",
        )}
      >
        —
      </span>

      <span role="gridcell" className={ADMIN_ORDERS_CELL_STATUS_CLASS}>
        <Badge variant={videoReviewBadgeVariant(video.reviewStatus)} dot>
          {t(
            video.reviewStatus === "REVISION_REQUESTED"
              ? "marketplace.partner.videos.status.needsRevision"
              : "marketplace.partner.videos.status.inReview",
            video.reviewStatus === "REVISION_REQUESTED"
              ? "Needs revision"
              : "In review",
          )}
        </Badge>
      </span>

      <span role="gridcell" aria-hidden className={ADMIN_ORDERS_CELL_SENT_CLASS} />
      <span role="gridcell" aria-hidden className={ADMIN_ORDERS_CELL_DUE_CLASS} />

      <span role="gridcell" className={ADMIN_ORDERS_CELL_ACTIONS_CLASS}>
        {video.scriptReviewId || video.preflightUrl ? (
          <span onClick={(event) => event.stopPropagation()}>
            <ScriptReviewAction
              scriptReviewId={video.scriptReviewId}
              preflightUrl={video.preflightUrl}
              label={t("marketplace.orders.list.review", "Review")}
              ariaLabel={t("marketplace.partner.videos.viewAria", {
                label,
                title: scriptTitle,
                defaultValue: "View review for {{label}} on {{title}}",
              })}
              variant="outline"
              size="sm"
              compact
            />
          </span>
        ) : null}
      </span>
    </li>
  );
}
